const SENSITIVE_HEADER_NAMES = new Set([
  "authorization",
  "access-api-key",
  "access-api-id",
  "cookie",
  "x-csrf-token",
  "x-xsrf-token",
]);

const SENSITIVE_PATH_FRAGMENTS = ["/login", "/password", "/pay", "/payment"];

const RESPONSE_BODY_MAX_LEN = 2048;

export function shouldCaptureApiStatus(status: number): boolean {
  return status !== 401;
}

export function resolveTenantFromBackendUrl(
  backendURL: string | null | undefined
): string | undefined {
  if (!backendURL) {
    return undefined;
  }

  try {
    return new URL(backendURL).hostname || undefined;
  } catch {
    return undefined;
  }
}

export function truncateResponseBody(text: string, maxLen = RESPONSE_BODY_MAX_LEN): string {
  if (!text) {
    return "";
  }

  return text.length > maxLen ? `${text.substring(0, maxLen)}...` : text;
}

export function parseReferenceId(body: unknown): string | undefined {
  if (!body || typeof body !== "object" || !("referenceId" in body)) {
    return undefined;
  }

  const referenceId = (body as { referenceId?: unknown }).referenceId;
  return referenceId == null ? undefined : String(referenceId);
}

export function isSensitivePath(url: string): boolean {
  let path = url;

  try {
    path = new URL(url).pathname;
  } catch {
    // Keep the original string when it is already a path fragment.
  }

  const normalizedPath = path.toLowerCase();
  return SENSITIVE_PATH_FRAGMENTS.some((fragment) =>
    normalizedPath.includes(fragment)
  );
}

function scrubHeaderRecord(
  headers: Record<string, unknown>
): Record<string, unknown> {
  const scrubbed: Record<string, unknown> = {};

  for (const [name, value] of Object.entries(headers)) {
    if (!SENSITIVE_HEADER_NAMES.has(name.toLowerCase())) {
      scrubbed[name] = value;
    }
  }

  return scrubbed;
}

export function scrubSentryEvent<T extends { request?: unknown }>(event: T): T {
  if (!event.request || typeof event.request !== "object") {
    return event;
  }

  const request = { ...(event.request as Record<string, unknown>) };
  const headers = request.headers;

  if (headers && typeof headers === "object") {
    request.headers = scrubHeaderRecord(headers as Record<string, unknown>);
  }

  const url = typeof request.url === "string" ? request.url : "";
  if (url && isSensitivePath(url)) {
    delete request.data;
    request.cookies = {};
  }

  return {
    ...event,
    request,
  };
}
